import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Cacheable } from 'ts-cacheable';
import Maintenance from '../../models/Maintenance';
import { MaintenanceFilesResponse } from '../../models/MaintenanceFilesResponse';
import MaintenanceInvoiceFile from '../../models/MaintenanceInvoiceFile';
import {
  MaintenanceResponse,
  MaintenanceRow,
} from '../../models/MaintenanceResponse';
import { BaseService } from '../base.service';

export const maintenanceToApproveCacheBuster$ = new Subject<void>();

export interface MaintenanceData {
  files: MaintenanceInvoiceFile[];
}
@Injectable({
  providedIn: 'root',
})
export class MaintenanceService extends BaseService {
  constructor(protected http: HttpClient) {
    super(http);
  }

  @Cacheable({
    maxAge: 1000 * 60 * 15,
    cacheBusterObserver: maintenanceToApproveCacheBuster$,
  })
  fetchMaintenanceToApprove(): Observable<Maintenance[]> {
    return this.callGet(`owner/maintenance`).pipe(
      map((response: MaintenanceResponse) => {
        if (response && response.data) {
          return response.data.map(Maintenance.parseFromData);
        }
        return [];
      }),
      catchError((response: HttpErrorResponse) => {
        return this.getResponseMessage(
          response,
          'Unable to fetch maintenance data'
        );
      })
    );
  }

  sendReport(data: any): Observable<any> {
    return this.callPost('owner/maintenance', data).pipe(
      catchError((response: HttpErrorResponse) => {
        return this.getResponseMessage(response, 'Unable to send report');
      })
    );
  }

  approve(maintenanceId: number, comments: string): Observable<any> {
    return this.callPut(`owner/maintenance/approve/${maintenanceId}`, {
      comments,
    }).pipe(
      catchError((response: HttpErrorResponse) => {
        return this.getResponseMessage(response, 'Unable to approve');
      })
    );
  }

  reject(maintenanceId: number, comments: string): Observable<any> {
    return this.callPut(`owner/maintenance/reject/${maintenanceId}`, {
      comments,
    }).pipe(
      catchError((response: HttpErrorResponse) => {
        return this.getResponseMessage(response, 'Unable to reject');
      })
    );
  }

  getDownloadUrl(id: number): Observable<string> {
    return this.callGet(`owner/download/${id}`).pipe(
      catchError((response: HttpErrorResponse) => {
        return this.getResponseMessage(response, 'Unable to get download file');
      })
    );
  }

  fetchData(areaId = null): Observable<MaintenanceData> {
    const url = areaId
      ? `owner/files/maintenance/${areaId}`
      : `owner/files/maintenance`;
    return this.callGet(url).pipe(
      map((response: MaintenanceFilesResponse) => {
        if (response && response.data) {
          return {
            files: response.data.files.map(
              MaintenanceInvoiceFile.parseFromData
            ),
          };
        }
        return {
          files: [],
          graph: [],
        };
      }),
      catchError((response: HttpErrorResponse) => {
        return this.getResponseMessage(
          response,
          'Unable to fetch maintenance data'
        );
      })
    );
  }

  fetchInvoices(
    areaId: number,
    date: string,
    limit: number = 100,
    skip: number = 0
  ): Observable<MaintenanceInvoiceFile[]> {
    return this.callGet(
      `owner/invoices?area_id=${areaId}&date=${date}&limit=${limit}&skip=${skip}`
    ).pipe(
      map((response: any) => {
        if (response && response.data) {
          return response.data.map(MaintenanceInvoiceFile.parseFromData);
        }
        return [];
      }),
      catchError((response: HttpErrorResponse) => {
        return this.getResponseMessage(response, 'Unable to fetch data');
      })
    );
  }

  fetchMaintenanceById(maintenanceId: number): Observable<Maintenance> {
    return this.callGet(`owner/maintenance/${maintenanceId}`).pipe(
      map((response: MaintenanceRow) => {
        if (response && response.data) {
          return Maintenance.parseFromData(response.data);
        }
        throwError(() => new Error('Unable to find maintenance record'));
      }),
      catchError((response: HttpErrorResponse) => {
        return this.getResponseMessage(
          response,
          'Unable to fetch maintenance record'
        );
      })
    );
  }
}
